import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { CheckCircle, Shield, AlertCircle } from 'lucide-react';

const CouncillorClaim = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      const response = await api.get(`/councillors/verify-claim?token=${token}`);
      if (response.data.valid) {
        setClaimData(response.data);
        
        if (response.data.is_claimed) {
          toast.info('This profile has already been claimed');
          setTimeout(() => navigate('/login'), 2000);
        }
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      toast.error('Invalid or expired claim link');
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  const handleClaim = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setClaiming(true);

    try {
      const response = await api.post('/councillors/complete-claim', {
        token,
        password,
      });

      toast.success('✅ Profile claimed successfully!');
      
      // Store the token
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }

      // Redirect to councillor dashboard
      setTimeout(() => {
        navigate('/councillor/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Error claiming profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim profile');
    } finally {
      setClaiming(false);
    }
  };

  if (loading || verifying) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <div className="text-white font-mono">VERIFYING CLAIM LINK...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!token) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="max-w-md text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">Invalid Claim Link</h1>
            <p className="text-gray-400 mb-6">
              This page requires a valid claim token. Please check your email for the correct link.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 rounded transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!claimData) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="max-w-md text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">Claim Link Expired</h1>
            <p className="text-gray-400 mb-6">
              This claim link has expired or is invalid. Please contact an administrator for a new link.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 rounded transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white py-12">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-6 p-4 bg-green-500/10 rounded-full">
              <Shield className="w-16 h-16 text-green-500" />
            </div>
            
            <h1 className="text-4xl font-bold mb-4 font-mono">
              CLAIM YOUR <span className="text-green-500">COUNCILLOR PROFILE</span>
            </h1>
            
            <p className="text-gray-400 text-lg">
              Set up your account to manage your profile and connect with your ward
            </p>
          </div>

          {/* Claim Info Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 mb-8">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold mb-2">{claimData.name}</h2>
                <p className="text-gray-400 text-sm mb-1">
                  <span className="text-green-500 font-mono">{claimData.ward}</span> Councillor
                </p>
                <p className="text-gray-500 text-sm">{claimData.email}</p>
              </div>
            </div>
          </div>

          {/* Password Form */}
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
            <h3 className="text-lg font-bold mb-6">Create Your Account Password</h3>
            
            <form onSubmit={handleClaim} className="space-y-6">
              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-gray-400 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:border-green-500 focus:outline-none"
                  placeholder="Minimum 8 characters"
                  data-testid="password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-gray-400 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:border-green-500 focus:outline-none"
                  placeholder="Re-enter your password"
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="bg-gray-800/50 border border-gray-700 rounded p-4">
                <p className="text-sm text-gray-300 mb-2 font-bold">Once claimed, you'll be able to:</p>
                <ul className="text-sm text-gray-400 space-y-1 ml-4">
                  <li>• Update your profile photo and biography</li>
                  <li>• Set your priorities and office hours</li>
                  <li>• Add comments to explain your votes</li>
                  <li>• View ward analytics and engagement</li>
                  <li>• Send email updates to ward residents</li>
                  <li>• Report any errors in your data</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={claiming}
                className="w-full bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-4 rounded transition-colors disabled:opacity-50 text-lg"
                data-testid="claim-profile-button"
              >
                {claiming ? 'CLAIMING PROFILE...' : 'CLAIM PROFILE'}
              </button>
            </form>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              Having trouble? Contact the CYQR admin team for assistance.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CouncillorClaim;
